import { useEffect, useMemo, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { getUsersPaged } from 'services/usersServices';
import { eppValidatorsI18n } from './i18n';

/**
 * Modal de edición de la configuración de UN validador EEPP.
 *
 * - Multi-select de los ~11 tipos de escritura agrupados por familia jurídica
 *   (checkboxes), con atajo "seleccionar familia entera" por grupo.
 * - Capacidad (tiempos) vía Input numérico (>= 0).
 * - Supervisor SLA (ComboBox con búsqueda de cualquier usuario activo — recibe
 *   los emails de escrituras vencidas del validador; opción "Sin supervisor"
 *   para limpiar). Mismo patrón de búsqueda (getUsersPaged + onInputChange) que
 *   AdminNegotiationSupervisors.
 * - Toggle "validador activo" (soft-inactive).
 *
 * No crea/borra usuarios: solo configura un usuario EEPP existente (upsert).
 * Los tipos vienen de getWritingTypes (code + displayName + familyJuridica).
 */
export default function ValidatorEditModal({
  DesignSystemComponents,
  show,
  validator,
  writingTypes = [],
  onClose,
  onSave,
}) {
  const { Modal, Input, ComboBox } = DesignSystemComponents || {};
  const { t } = useScopedTranslation(eppValidatorsI18n);

  const [selectedCodes, setSelectedCodes] = useState([]);
  const [capacity, setCapacity] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Supervisor SLA: option {label, value} (value = userId) | null (sin supervisor).
  const [supervisor, setSupervisor] = useState(null);
  const [supervisorSearch, setSupervisorSearch] = useState('');
  const [supervisorOptions, setSupervisorOptions] = useState([]);
  const [loadingSupervisors, setLoadingSupervisors] = useState(false);

  useEffect(() => {
    if (!show) return;
    setSelectedCodes(
      Array.isArray(validator?.writingTypeCodes)
        ? validator.writingTypeCodes
        : []
    );
    setCapacity(
      validator?.capacityTiempos != null
        ? String(validator.capacityTiempos)
        : ''
    );
    setIsActive(validator?.isActive ?? true);
    // Precarga el supervisor actual como option (el backend da id + name).
    setSupervisor(
      validator?.supervisorUserId != null
        ? {
            label:
              validator.supervisorUserName ||
              String(validator.supervisorUserId),
            value: validator.supervisorUserId,
          }
        : null
    );
    setSupervisorSearch('');
    setSupervisorOptions([]);
  }, [show, validator]);

  // Búsqueda dinámica de usuarios (debounce 400ms, mín. 3 caracteres). Cualquier
  // usuario activo puede ser supervisor (jefatura puede estar fuera del área EEPP).
  useEffect(() => {
    if (!show) return undefined;
    const deb = setTimeout(async () => {
      if (!supervisorSearch || supervisorSearch.length < 3) {
        setSupervisorOptions([]);
        return;
      }
      setLoadingSupervisors(true);
      try {
        const result = await getUsersPaged(1, 20, true, supervisorSearch);
        setSupervisorOptions(
          (result?.users || []).map((u) => ({
            label: u.email ? `${u.name} (${u.email})` : u.name,
            value: u.id,
          }))
        );
      } catch {
        setSupervisorOptions([]);
      } finally {
        setLoadingSupervisors(false);
      }
    }, 400);
    return () => clearTimeout(deb);
  }, [supervisorSearch, show]);

  // Agrupar los tipos por familia jurídica (para el multi-select agrupado).
  const groups = useMemo(() => {
    const byFamily = new Map();
    (writingTypes || []).forEach((typ) => {
      const family = typ.familyJuridica || '__uncategorized__';
      if (!byFamily.has(family)) byFamily.set(family, []);
      byFamily.get(family).push(typ);
    });
    return Array.from(byFamily.entries()).map(([family, types]) => ({
      family,
      label:
        family === '__uncategorized__'
          ? t('eepp_validators_family_uncategorized')
          : family,
      types,
    }));
  }, [writingTypes, t]);

  const selectedSet = useMemo(
    () => new Set(selectedCodes.map((c) => String(c).toUpperCase())),
    [selectedCodes]
  );

  const isCodeSelected = (code) => selectedSet.has(String(code).toUpperCase());

  const toggleCode = (code) => {
    setSelectedCodes((prev) => {
      const upper = String(code).toUpperCase();
      const exists = prev.some((c) => String(c).toUpperCase() === upper);
      return exists
        ? prev.filter((c) => String(c).toUpperCase() !== upper)
        : [...prev, code];
    });
  };

  // Atajo: seleccionar/deseleccionar todos los tipos de una familia.
  const toggleFamily = (types) => {
    const codes = types.map((typ) => typ.code);
    const allSelected = codes.every((c) => isCodeSelected(c));
    setSelectedCodes((prev) => {
      const upperCodes = new Set(codes.map((c) => String(c).toUpperCase()));
      const withoutFamily = prev.filter(
        (c) => !upperCodes.has(String(c).toUpperCase())
      );
      return allSelected ? withoutFamily : [...withoutFamily, ...codes];
    });
  };

  const capacityNumber = Number(capacity);
  const capacityValid =
    capacity !== '' && Number.isFinite(capacityNumber) && capacityNumber >= 0;
  const isValid = capacityValid;

  const handleSave = () => {
    if (!isValid) return;
    onSave?.({
      userId: validator?.userId,
      writingTypeCodes: selectedCodes,
      capacityTiempos: capacityNumber,
      // null = sin supervisor (limpia la asignación).
      supervisorUserId: supervisor?.value ?? null,
      isActive,
    });
  };

  if (!show || !Modal || !Input) return null;

  return (
    <Modal
      title={`${t('eepp_validators_modal_title')}${validator?.userName ? ` · ${validator.userName}` : ''}`}
      show={show}
      onClose={onClose}
      primaryBtnTxt={t('eepp_validators_modal_primary')}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid}
      secondaryBtnTxt={t('eepp_validators_cancel')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='640px'
    >
      <div className='row g-3'>
        <div className='col-12'>
          <label className='form-label fw-semibold' style={{ fontSize: 13 }}>
            {t('eepp_validators_modal_types_label')}
          </label>
          {groups.map((group) => {
            const allSelected =
              group.types.length > 0 &&
              group.types.every((typ) => isCodeSelected(typ.code));
            return (
              <div key={group.family} className='mb-3'>
                <div
                  className='d-flex justify-content-between align-items-center mb-1'
                  style={{
                    borderBottom: '1px solid #E2E8F0',
                    paddingBottom: 4,
                  }}
                >
                  <span
                    className='fw-semibold'
                    style={{ fontSize: 12, color: '#475569' }}
                  >
                    {group.label}
                  </span>
                  <button
                    type='button'
                    className='btn btn-link btn-sm p-0'
                    style={{ fontSize: 11 }}
                    onClick={() => toggleFamily(group.types)}
                  >
                    {allSelected ? '— ' : '+ '}
                    {t('eepp_validators_modal_select_family')}
                  </button>
                </div>
                <div className='d-flex flex-wrap' style={{ gap: 8 }}>
                  {group.types.map((typ) => (
                    <label
                      key={typ.code}
                      className='d-flex align-items-center'
                      style={{
                        gap: 6,
                        fontSize: 12,
                        cursor: 'pointer',
                        padding: '4px 8px',
                        border: '1px solid #E2E8F0',
                        borderRadius: 6,
                        background: isCodeSelected(typ.code)
                          ? '#EFF6FF'
                          : '#fff',
                      }}
                    >
                      <input
                        type='checkbox'
                        checked={isCodeSelected(typ.code)}
                        onChange={() => toggleCode(typ.code)}
                      />
                      <span>{typ.displayName || typ.code}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className='col-12 col-md-6'>
          <Input
            label={t('eepp_validators_modal_capacity_label')}
            type='number'
            min={0}
            placeholder={t('eepp_validators_modal_capacity_placeholder')}
            value={capacity}
            onChange={(e) =>
              setCapacity(e?.target ? e.target.value : (e ?? ''))
            }
            error={
              capacity !== '' && !capacityValid
                ? t('eepp_validators_modal_capacity_invalid')
                : ''
            }
            helperText={t('eepp_validators_modal_capacity_helper')}
          />
        </div>

        {/* Supervisor SLA: cualquier usuario activo; "Sin supervisor" limpia. */}
        <div className='col-12 col-md-6'>
          {ComboBox ? (
            <ComboBox
              label={t('ev_supervisor_label')}
              placeholder={t('ev_supervisor_placeholder')}
              noResultText={t('ev_supervisor_none')}
              value={supervisor}
              onChange={(option) => setSupervisor(option || null)}
              onInputChange={(text) => setSupervisorSearch(text)}
              options={supervisorOptions}
              isLoading={loadingSupervisors}
              isClearable
              width='100%'
            />
          ) : (
            <>
              <label
                className='form-label fw-semibold'
                style={{ fontSize: 13 }}
              >
                {t('ev_supervisor_label')}
              </label>
              <input
                type='text'
                className='form-control form-control-sm'
                placeholder={t('ev_supervisor_placeholder')}
                value={supervisorSearch}
                onChange={(e) => setSupervisorSearch(e.target.value)}
              />
              {supervisorOptions.length > 0 && (
                <ul
                  className='list-group mt-1'
                  style={{ fontSize: 13, maxHeight: 200, overflowY: 'auto' }}
                >
                  {supervisorOptions.map((u) => (
                    <li
                      key={u.value}
                      className='list-group-item list-group-item-action py-1'
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSupervisor(u)}
                    >
                      {u.label}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div className='col-12 d-flex align-items-center'>
          <label
            className='d-flex align-items-center'
            style={{ gap: 8, cursor: 'pointer' }}
          >
            <input
              type='checkbox'
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span style={{ fontSize: 13 }}>
              {t('eepp_validators_modal_active_label')}
            </span>
          </label>
        </div>
      </div>
    </Modal>
  );
}
